'use client';

import { useEffect, useRef, useState } from 'react';
import { EndpointResult } from './types';
import { formatTs, nanoToMs } from './timeUtils';

interface Props {
  result: EndpointResult | null;
  cursorX: number;
  cursorY: number;
}

export default function Tooltip({ result, cursorX, cursorY }: Props) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!result || !tooltipRef.current) return;
    const tt = tooltipRef.current.getBoundingClientRect();
    const offset = 14;
    let left = cursorX + offset;
    let top = cursorY + offset;
    if (left + tt.width + 10 > window.innerWidth) left = cursorX - tt.width - offset;
    if (top + tt.height + 10 > window.innerHeight) top = cursorY - tt.height - offset;
    const next = { top: Math.round(top), left: Math.round(left) };
    const id = setTimeout(() => setPos(next), 0);
    return () => clearTimeout(id);
  }, [cursorX, cursorY, result]);

  if (!result) return null;

  return (
    <div
      ref={tooltipRef}
      className="fixed z-50 px-3 py-2 text-sm rounded-lg shadow-xl border bg-bg-elevated border-white/10 min-w-[200px] max-w-[280px] pointer-events-none"
      style={{ top: pos.top, left: pos.left }}
    >
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className={`inline-block w-2 h-2 rounded-full ${result.success ? 'bg-green-400' : 'bg-red-400'}`} />
          <span className={`text-xs font-semibold ${result.success ? 'text-green-400' : 'text-red-400'}`}>{result.success ? 'Success' : 'Failure'}</span>
        </div>
        <div>
          <div className="text-xs font-semibold text-text-muted uppercase tracking-wider">Timestamp</div>
          <div className="font-mono text-xs text-text-secondary">{formatTs(result.timestamp)}</div>
        </div>
        <div>
          <div className="text-xs font-semibold text-text-muted uppercase tracking-wider">Response Time</div>
          <div className="font-mono text-xs text-text-secondary">{nanoToMs(result.duration)}ms</div>
        </div>
        {result.status > 0 && (
          <div>
            <div className="text-xs font-semibold text-text-muted uppercase tracking-wider">Status Code</div>
            <div className="font-mono text-xs text-text-secondary">{result.status}</div>
          </div>
        )}
        {result.errors?.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-text-muted uppercase tracking-wider">Errors</div>
            <div className="font-mono text-xs space-y-0.5 mt-0.5">
              {result.errors.map((e, i) => (
                <div key={i} className="text-red-400">
                  • {e}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
